<template>
  <form :class="classes()" @submit.prevent="handleSubmit">
    <Copy
      v-if="statusCode === 401"
      size="m"
      color="supportDanger"
      centered
      bold
      responsive
      staticClass="u-margin__bottom--4"
    >
      {{ $t('profile.forms.error.generics') }}
    </Copy>

    <Row class="u-margin__top--1">
      <Col :xs="6" hasMargin>
        <Copy element="div" size="s" color="tertiaryLight" :staticClass="`${el}__formLabel`">
          {{ $t('careProfile.form.label.gender') }}
          <Icon
            name="information-line"
            color="primaryBlue"
            :content="$t('careProfile.form.info.gender')"
            v-tippy="{ arrow: true }"
            isButton
            @mouseenter="() => onInfoIconClick($t('careProfile.form.label.gender'))"
            :onClick="() => onInfoIconClick($t('careProfile.form.label.gender'))"
          />
        </Copy>
        <BaseSelect
          name="gender"
          :items="$t('careProfile.form.value.gender')"
          :activeValue="$t('careProfile.form.value.gender')[formFields.gender.value].value"
          @input="setSalutation"
          :error="formFields.gender.error"
          expand
        />
      </Col>
    </Row>

    <Row>
      <Col :xs="12" :m="6" hasMargin>
        <BaseInput
          name="firstname"
          :label="$t('careProfile.form.label.firstname')"
          v-model.trim="formFields.firstname.value"
          :error="formFields.firstname.error"
          @input="input(formFields.firstname)"
          clearable
        />
      </Col>
      <Col :xs="12" :m="6" hasMargin>
        <BaseInput
          name="lastname"
          :label="$t('careProfile.form.label.lastname')"
          v-model.trim="formFields.lastname.value"
          :error="formFields.lastname.error"
          @input="input(formFields.lastname)"
          clearable
        />
      </Col>
    </Row>

    <Row>
      <Col :xs="12" :m="9" hasMargin>
        <BaseInput
          name="street"
          :label="$t('careProfile.form.label.street')"
          v-model.trim="formFields.street.value"
          :error="formFields.street.error"
          @input="input(formFields.street)"
          clearable
        />
      </Col>

      <Col :xs="12" :m="3" hasMargin>
        <BaseInput
          name="houseNumber"
          :label="$t('careProfile.form.label.houseNumber')"
          v-model.trim="formFields.houseNumber.value"
          :error="formFields.houseNumber.error"
          @input="input(formFields.houseNumber)"
          clearable
        />
      </Col>
    </Row>

    <Row>
      <Col :xs="12" :m="4" hasMargin>
        <BaseInput
          name="zipCode"
          :label="$t('careProfile.form.label.zipCode')"
          v-model.trim="formFields.zipCode.value"
          :error="formFields.zipCode.error"
          @input="input(formFields.zipCode)"
          clearable
        />
      </Col>
      <Col :xs="12" :m="8" hasMargin>
        <BaseInput
          name="city"
          :label="$t('careProfile.form.label.city')"
          v-model.trim="formFields.city.value"
          :error="formFields.city.error"
          @input="input(formFields.city)"
          clearable
        />
      </Col>
    </Row>

    <Divider dotted class="u-margin__top--2 u-margin__bottom--6" />

    <Row>
      <Col :xs="12" :m="6" hasMargin class="u-margin__top--1">
        <Copy
          element="div"
          size="s"
          color="tertiaryLight"
          :staticClass="`${el}__formLabel u-margin__bottom--1`"
        >
          {{ $t('careProfile.form.label.birthdate') }}
        </Copy>
        <Datepicker
          v-model="formFields.birthdate.value"
          :language="de"
          :format="$t('datepicker.format')"
          :placeholder="$t('datepicker.placeholder')"
          :open-date="formFields.birthdate.value"
          :disabledDates="disabledDates"
          @selected="setBirthdate"
        />
      </Col>

      <Col :xs="12" :m="6" hasMargin>
        <Copy element="div" size="s" color="tertiaryLight" :staticClass="`${el}__formLabel`">
          {{ $t('careProfile.form.label.typeOfResidency') }}
          <Icon
            name="information-line"
            color="primaryBlue"
            :content="$t('careProfile.form.info.typeOfResidency')"
            v-tippy="{ arrow: true }"
            isButton
            @mouseenter="() => onInfoIconClick($t('careProfile.form.label.typeOfResidency'))"
            :onClick="() => onInfoIconClick($t('careProfile.form.label.typeOfResidency'))"
          />
        </Copy>
        <BaseSelect
          name="typeOfResidency"
          :items="$t('careProfile.form.value.typeOfResidency')"
          :activeValue="formFields.typeOfResidency.value"
          @input="setTypeOfResidency"
          expand
        />
      </Col>
    </Row>

    <Row>
      <Col :xs="12" :m="6" hasMargin>
        <Copy element="div" size="s" color="tertiaryLight" :staticClass="`${el}__formLabel`">
          {{ $t('careProfile.form.label.insuranceNumber') }}
          <Icon
            name="information-line"
            color="primaryBlue"
            :content="$t('careProfile.form.info.insuranceNumber')"
            v-tippy="{ arrow: true }"
            isButton
            @mouseenter="() => onInfoIconClick($t('careProfile.form.label.insuranceNumber'))"
            :onClick="() => onInfoIconClick($t('careProfile.form.label.insuranceNumber'))"
          />
        </Copy>
        <BaseInput
          name="insuranceNumber"
          v-model="formFields.insuranceNumber.value"
          :error="formFields.insuranceNumber.error"
          @input="input(formFields.insuranceNumber)"
          clearable
        />
      </Col>

      <Col :xs="12" :m="6">
        <Copy element="div" size="s" color="tertiaryLight" :staticClass="`${el}__formLabel`">
          {{ $t('careProfile.form.label.careFund') }}
          <Icon
            name="information-line"
            color="primaryBlue"
            :content="$t('careProfile.form.info.careFund')"
            v-tippy="{ arrow: true }"
            isButton
            @mouseenter="() => onInfoIconClick($t('careProfile.form.label.careFund'))"
            :onClick="() => onInfoIconClick($t('careProfile.form.label.careFund'))"
          />
        </Copy>
        <HealthInsurances
          :activeInsurance="formFields.careFund.value"
          :showAddress="false"
          @insuranceSet="setInsurance"
        />
      </Col>
    </Row>

    <div :class="`${el}__buttonRow`">
      <Button
        :label="$t('careProfile.form.button.cancel')"
        type="outlined"
        @click="onCancelClick"
        small
      />
      <Button
        :label="$t('careProfile.form.button.save')"
        type="primary"
        :loading="isLoading"
        submit
        small
      />
    </div>
  </form>
</template>

<script>
import Button from '@/objects/Button';
import Divider from '@/objects/Divider';
import Col from '@/components/Grid/Col';
import Row from '@/components/Grid/Row';
import Copy from '@/objects/Copy';
import BaseInput from '@/objects/Forms/BaseInput';
import BaseSelect from '@/objects/Forms/BaseSelect';
import HealthInsurances from '@/components/HealthInsurances';
import Icon from '@/objects/Icon';
import Datepicker from 'vuejs-datepicker';

import { USER_CHANGE_REQUEST } from '@/store/actions/user';

import { ROUTE_API_CHANGE_USERDATA, ROUTE_API_UPDATE_CARE_PROFILE } from '@/constants/apiRoutes';

import { HEALTH_REQUEST } from '@/store/actions/health';

import {
  cityPattern,
  houseNumberPattern,
  namePattern,
  streetPattern,
  zipCodePattern,
  insuranceNumberPattern,
} from '@/helpers/patterns';

import { formatDate } from '@/helpers/formatter';
import { de } from 'vuejs-datepicker/dist/locale';

import Vue from 'vue';
import { validateForm } from '@/helpers/formValidation';
import { mapFieldsHealth } from '@/store/mapFields';
import { createValidationData } from '@/helpers/tracking/validationTracking';

export default {
  name: 'CareProfileForm',
  components: {
    Copy,
    Datepicker,
    Icon,
    HealthInsurances,
    BaseInput,
    BaseSelect,
    Row,
    Col,
    Button,
    Divider,
  },
  props: {
    profileData: {
      type: Object,
      required: true,
    },
  },

  created() {
    this.setFormFields(this.profileData);
    window.addEventListener('beforeunload', this.beforeWindowUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  },

  watch: {
    profileDataChanged: function () {
      this.$emit('dataChanged', this.profileDataChanged);
    },
  },
  computed: {
    ...mapFieldsHealth(['healthUuid']),
  },

  data() {
    return {
      formatDate,
      profileDataChanged: false,
      isProfileOwner: null,
      loadData: false,
      statusCode: '',
      formFields: {
        firstname: {
          value: '',
          error: null,
          required: false,
          meta: 'firstname',
          pattern: {
            value: namePattern,
            message: this.$t('careProfile.form.error.firstname.formatted'),
          },
        },
        lastname: {
          value: '',
          error: null,
          required: false,
          meta: 'lastname',
          pattern: {
            value: namePattern,
            message: this.$t('careProfile.form.error.lastname.formatted'),
          },
        },
        birthdate: {
          value: '',
          error: null,
          required: false,
          meta: 'birthdate',
        },
        street: {
          value: '',
          error: null,
          required: false,
          meta: 'street',
          pattern: {
            value: streetPattern,
            message: this.$t('careProfile.form.error.street.formatted'),
          },
        },
        houseNumber: {
          value: '',
          error: null,
          required: false,
          meta: 'houseNumber',
          pattern: {
            value: houseNumberPattern,
            message: this.$t('careProfile.form.error.houseNumber.formatted'),
          },
        },
        zipCode: {
          value: '',
          error: null,
          required: false,
          meta: 'zipCode',
          pattern: {
            value: zipCodePattern,
            message: this.$t('careProfile.form.error.zipCode.formatted'),
          },
        },
        city: {
          value: '',
          error: null,
          required: false,
          meta: 'city',
          pattern: {
            value: cityPattern,
            message: this.$t('careProfile.form.error.city.formatted'),
          },
        },
        typeOfResidency: {
          value: '',
          error: null,
          required: false,
          meta: 'typeOfResidency',
        },
        gender: {
          value: '',
          error: null,
          required: false,
          pattern: {
            value: '',
          },
          meta: 'gender',
        },
        insuranceNumber: {
          value: '',
          error: null,
          required: false,
          meta: 'insuranceNumber',
          pattern: {
            value: insuranceNumberPattern,
            message: this.$t('careProfile.form.error.insuranceNumber.formatted'),
          },
        },
        careFund: {
          value: '',
          label: '',
          key: '',
          error: null,
          required: false,
          meta: 'careFund',
        },
      },
      el: 'c-careProfileForm',
      isLoading: false,
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
      de,
      disabledDates: {
        from: new Date(Date.now()),
      },
    };
  },
  methods: {
    setFormFields(profileData) {
      this.formFields.gender.value = profileData.gender ? profileData.gender : 0;
      this.formFields.firstname.value = profileData.firstname ? profileData.firstname : '';
      this.formFields.lastname.value = profileData.lastname ? profileData.lastname : '';
      this.formFields.birthdate.value = profileData.birthdate
        ? new Date(profileData.birthdate)
        : null;
      this.formFields.street.value = profileData.street ? profileData.street : '';
      this.formFields.city.value = profileData.city ? profileData.city : '';
      this.formFields.insuranceNumber.value = profileData.insuranceNumber
        ? profileData.insuranceNumber
        : '';
      this.formFields.typeOfResidency.value = profileData.typeOfResidency
        ? profileData.typeOfResidency
        : '';
      this.isProfileOwner = profileData.isProfileOwner ? profileData.isProfileOwner : false;
      this.formFields.zipCode.value = profileData.zipCode ? profileData.zipCode : '';
      this.formFields.houseNumber.value = profileData.houseNumber
        ? profileData.houseNumber.toString()
        : '';
      this.formFields.careFund.value = profileData.careFund ? parseInt(profileData.careFund.id) : 0;
      this.formFields.careFund.label = profileData.careFund ? profileData.careFund.name : '';
      this.formFields.careFund.key = profileData.careFund ? profileData.careFund.care_fund_key : '';
    },

    handleSubmit(e) {
      this.isLoading = true;
      this.checkRequiredFields();
      const { isValid } = validateForm(e.target.elements, this.formFields);

      if (isValid && !this.formFields.gender.error) {
        let accountUserChanged = false;

        const user = {
          firstname: this.formFields.firstname.value,
          lastname: this.formFields.lastname.value,
          gender: this.formFields.gender.value,
        };

        const address = {
          street: this.formFields.street.value,
          houseNumber: this.formFields.houseNumber.value,
          city: this.formFields.city.value,
          zipCode: this.formFields.zipCode.value,
        };

        const formData = {
          firstname: user.firstname,
          lastname: user.lastname,
          gender: this.formFields.gender.value,
          birthdate: this.formFields.birthdate.value,
          typeOfResidency: this.formFields.typeOfResidency.value,
          insuranceNumber: this.formFields.insuranceNumber.value,
          careFundKey: this.formFields.careFund.key,
        };

        const axiosData = [
          {
            url: ROUTE_API_UPDATE_CARE_PROFILE,
            data: {
              careProfile: { ...user, ...formData, ...address },
              personInNeedOfCareUuid: this.healthUuid,
            },
          },
        ];

        if (
          (this.formFields.firstname.value !== this.profileData.firstname ||
            this.formFields.lastname.value !== this.profileData.lastname ||
            this.formFields.gender.value !== this.profileData.gender) &&
          this.isProfileOwner
        ) {
          accountUserChanged = true;
          axiosData.push({
            url: ROUTE_API_CHANGE_USERDATA,
            data: { ...user },
          });
        }

        Promise.all(axiosData.map((data) => Vue.axios.post(data.url, data.data)))
          .then((response) => {
            this.statusCode = response.status;
            this.$store.dispatch(HEALTH_REQUEST);
            if (accountUserChanged) {
              this.$store.dispatch(USER_CHANGE_REQUEST, user);
            }
            this.$emit(
              'onTrackingClick',
              '-PD-Bearbeiten_btn-' + this.$t('careProfile.form.button.save'),
              'button'
            );
            this.changeEditMode();
          })
          .catch((error) => {
            // Error-Codes
            // 401: something went wrong
            // 422: validation error

            if (error.response) {
              const {
                data: { errors },
                status,
              } = error.response;

              this.statusCode = status;

              if (status === 422) {
                Object.keys(errors).map((error) => {
                  const type = errors[error][0];
                  const field = error.replace('careProfile.', '');
                  this.formFields[field].error = this.$t(`careProfile.form.error.${field}.${type}`);
                });
              }
            } else {
              this.statusCode = 401;
            }
            this.isLoading = false;
            this.profileDataChanged = false;
          });
      } else {
        createValidationData(this.formFields);
        this.isLoading = false;
      }
    },

    setSalutation(value) {
      this.input(this.formFields.gender);
      this.formFields.gender.value = value;
    },
    setTypeOfResidency(value) {
      this.input(this.formFields.typeOfResidency);
      this.formFields.typeOfResidency.value = value;
    },
    input(field) {
      const fieldName = this.profileData + '.' + field.meta;
      if (field.meta !== fieldName) {
        this.profileDataChanged = true;
      }
      this.clearError(field);
    },
    clearError(field) {
      field.error = null;
    },
    setInsurance(value) {
      this.input(this.formFields.careFund);
      this.formFields.careFund.value = value.value;
      this.formFields.careFund.label = value.label;
      this.formFields.careFund.key = value.key;
    },

    changeEditMode() {
      this.profileDataChanged = false;
      this.isLoading = false;
      this.$emit('dataChanged', this.profileDataChanged);
      this.$emit('close');
    },
    setBirthdate() {
      this.input(this.formFields.birthdate);
    },

    checkRequiredFields() {
      if (this.profileData.isProfileOwner) {
        this.formFields.firstname.required = this.$t('careProfile.form.error.firstname.required');
        this.formFields.lastname.required = this.$t('careProfile.form.error.lastname.required');
        this.formFields.gender.required = this.$t('careProfile.form.error.houseNumber.required');
        if (
          !(
            parseInt(this.formFields.gender.value) === 1 ||
            parseInt(this.formFields.gender.value) === 2
          )
        ) {
          this.formFields.gender.error = this.$t('careProfile.form.error.gender.required');
        } else {
          this.formFields.gender.error = null;
        }
      }
    },

    beforeWindowUnload(event) {
      if (this.profileDataChanged) {
        // Cancel the event
        event.preventDefault();
        // Chrome requires returnValue to be set
        event.returnValue = '';
      }
    },

    onCancelClick() {
      this.$emit(
        'onTrackingClick',
        '-PD-Bearbeiten_btn-' + this.$t('careProfile.form.button.cancel'),
        'button'
      );
      this.changeEditMode();
    },

    onInfoIconClick(label) {
      this.$emit('onTrackingClick', '-PD-Bearbeiten_content-InfoIconShown_' + label);
    },
  },
};
</script>
